// © Thibault Sabathier, 2026. frontend/src/components/messaging/AttachmentTray.js
//
// Bandeau des pièces jointes EN ATTENTE d'envoi, au-dessus de la zone de
// saisie. Purement présentationnel : tout l'état vit dans
// `useAttachmentComposer`.
//
// ── POURQUOI LES ENTRÉES REFUSÉES SONT AFFICHÉES ──────────────────────────
// Un fichier écarté silencieusement est le pire des cas : l'utilisateur croit
// avoir joint cinq photos et n'en envoie que quatre. Chaque refus reste donc
// visible, à côté du fichier concerné, avec sa raison — et se retire d'un clic.

import React from 'react'
import PropTypes from 'prop-types'
import CIcon from '@coreui/icons-react'
import { cilX, cilFile, cilWarning } from '@coreui/icons'
import { CProgress, CSpinner } from '@coreui/react'
import { useTranslation } from 'react-i18next'
import { formatFileSize } from '../../utils/messageAttachments'

const AttachmentTray = ({ entries, onRemove, limits, preparing, uploadPercent, disabled }) => {
  const { t } = useTranslation()

  const uploading = typeof uploadPercent === 'number'
  if (entries.length === 0 && !preparing && !uploading) return null

  return (
    <div className="attachment-tray" aria-live="polite">
      {preparing && (
        <div className="attachment-tray__status">
          <CSpinner size="sm" />
          <span>{t('messaging:attachments.preparing')}</span>
        </div>
      )}

      {entries.length > 0 && (
        <ul className="attachment-tray__list">
          {entries.map((entry) => {
            const rejected = entry.status === 'rejected'
            return (
              <li
                key={entry.id}
                className={`attachment-chip ${rejected ? 'attachment-chip--rejected' : ''}`}
              >
                <div className="attachment-chip__visual">
                  {entry.previewUrl ? (
                    // `alt` porte le nom réel : un lecteur d'écran doit pouvoir
                    // distinguer deux images jointes au même message.
                    <img
                      src={entry.previewUrl}
                      alt={t('messaging:attachments.image_alt', { name: entry.name })}
                    />
                  ) : (
                    <CIcon icon={rejected ? cilWarning : cilFile} />
                  )}
                </div>

                <div className="attachment-chip__meta">
                  <span className="attachment-chip__name" title={entry.name}>
                    {entry.name}
                  </span>
                  <span className="attachment-chip__detail">
                    {rejected
                      ? t(`messaging:attachments.errors.${entry.code}`, { name: entry.name })
                      : formatFileSize(entry.size)}
                  </span>
                </div>

                <button
                  type="button"
                  className="attachment-chip__remove"
                  onClick={() => onRemove(entry.id)}
                  // Retirer reste possible pendant l'envoi ? Non : le fichier
                  // est déjà dans la requête, le retirer ne l'annulerait pas
                  // et laisserait croire le contraire.
                  disabled={disabled || uploading}
                  title={t('messaging:attachments.remove', { name: entry.name })}
                  aria-label={t('messaging:attachments.remove', { name: entry.name })}
                >
                  <CIcon icon={cilX} />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {limits?.code && (
        <div className="attachment-tray__error" role="alert">
          <CIcon icon={cilWarning} />
          <span>{t(`messaging:attachments.errors.${limits.code}`)}</span>
        </div>
      )}

      {uploading && (
        <div className="attachment-tray__progress">
          <CProgress value={uploadPercent} height={4} />
          <span>{t('messaging:attachments.uploading', { percent: uploadPercent })}</span>
        </div>
      )}
    </div>
  )
}

AttachmentTray.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      size: PropTypes.number,
      status: PropTypes.oneOf(['ready', 'rejected']).isRequired,
      code: PropTypes.string,
      previewUrl: PropTypes.string,
    }),
  ).isRequired,
  onRemove: PropTypes.func.isRequired,
  limits: PropTypes.shape({ code: PropTypes.string }),
  preparing: PropTypes.bool,
  uploadPercent: PropTypes.number,
  disabled: PropTypes.bool,
}

export default AttachmentTray
